import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import moment from 'moment';
import { FaArrowLeft, FaArrowRight, FaDownload } from 'react-icons/fa';
import Fancybox from '../../helpers/Fancybox';
import PageHeader from '../../components/common/PageHeader';
import ApiService from '../../api/ApiService';
import '../../css/darshan.css';
import GoToTop from '../../helpers/GoToTop';
import LightGallery from 'lightgallery/react';

// import styles
import 'lightgallery/css/lightgallery.css';
import 'lightgallery/css/lg-zoom.css';
import 'lightgallery/css/lg-thumbnail.css';
import 'lightgallery/css/lg-autoplay.css';
import 'lightgallery/css/lg-fullscreen.css';
import 'lightgallery/css/lg-share.css';
import 'lightgallery/css/lg-rotate.css';


// import plugins if you need
import lgThumbnail from 'lightgallery/plugins/thumbnail';
import lgZoom from 'lightgallery/plugins/zoom';
import lgAutoplay from 'lightgallery/plugins/autoplay'
import lgFullscreen from 'lightgallery/plugins/fullscreen';
import lgShare from 'lightgallery/plugins/share';
import lgRotate from 'lightgallery/plugins/rotate';
const GalleryDetailPage = () => {
    const { type, date } = useParams();
    const navigate = useNavigate();
    const [currentSlide, setCurrentSlide] = useState(0);
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [festivalTitle, setFestivalTitle] = useState('');
    const [top, setTop] = useState(0);
    const [availableDates, setAvailableDates] = useState([]);
    const [actualDate, setActualDate] = useState(date);
    const today = moment().startOf('day');
    const festRef = useCallback((node) => {
        if (node) {
            setTop(node.offsetTop - 115);
        }
    }, []);

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % (images.length || 1));
        });
        return () => clearInterval(interval);
    }, [images.length]);

    useEffect(() => {
        const fetchGalleries = async () => {
            setLoading(true);
            setError(null);

            try {
                const response = await ApiService.fetchDailyDarshanGalleries();
                const galleryTypeData = response?.[type?.toLowerCase()];

                if (!galleryTypeData) {
                    setError(`No gallery found for type: ${type}`);
                    setImages([]);
                    return;
                }

                const dateMap = new Map();
                for (const entry of galleryTypeData) {
                    for (const img of entry.images) {
                        const formattedDate = moment(img.uploadedAt).format('YYYY-MM-DD');
                        if (!dateMap.has(formattedDate)) dateMap.set(formattedDate, []);
                        dateMap.get(formattedDate).push({
                            url: `${process.env.REACT_APP_S3_URL}/uploads/${type}/${img.filename}`,
                            caption: img.caption || `${type?.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} - ${formattedDate}`,
                        });
                    }
                }

                const sortedDates = Array.from(dateMap.keys()).sort((a, b) => moment(b).diff(moment(a)));
                setAvailableDates(sortedDates);

                let foundImages = [];
                let foundDate = date;

                if (type === 'festival-darshan') {
                    for (const d of sortedDates) {
                        if (moment(d).isSameOrBefore(moment(date))) {
                            foundImages = dateMap.get(d);
                            foundDate = d;
                            break;
                        }
                    }

                    // Set festival title for festival-darshan
                    if (foundImages.length > 0 && galleryTypeData.length > 0) {
                        const foundEntry = galleryTypeData.find(entry =>
                            entry.images.some(img =>
                                moment(img.uploadedAt).format('YYYY-MM-DD') === foundDate
                            )
                        );
                        if (foundEntry && foundEntry.festival_title) {
                            setFestivalTitle(foundEntry.festival_title);
                        } else {
                            setFestivalTitle('');
                        }
                    }
                } else {
                    foundImages = dateMap.get(moment(date).format('YYYY-MM-DD')) || [];
                }

                if (foundImages.length === 0) {
                    if (type === 'festival-darshan') {
                        setError('Festival Darshan images are not available for the selected date.');
                    } else {
                        let errorMessage = '';
                        switch (type) {
                            case 'mangala':
                                errorMessage = 'Mangala Darshan images are not available for the selected date.';
                                break;
                            case 'sringar':
                                errorMessage = 'Sringar Darshan images are not available for the selected date.';
                                break;
                            default:
                                errorMessage = `No images found for ${type?.replace(/-/g, ' ')} on ${moment(date).format('MMMM D, YYYY')}`;
                        }
                        setError(errorMessage);
                    }
                } else {
                    setError(null);
                }

                setImages(foundImages);
                setActualDate(foundDate);
            } catch (error) {
                console.error('Error fetching galleries:', error);
                setError('Failed to load gallery images. Please try again later.');
                setImages([]);
            } finally {
                setLoading(false);
            }
        };

        fetchGalleries();
    }, [type, date]);

    const handlePrevAvailable = () => {
        const currentIndex = availableDates.findIndex(d => d === actualDate);
        if (currentIndex + 1 < availableDates.length) {
            const prevDate = availableDates[currentIndex + 1];
            navigate(`/daily-darshan-gallery/${type}/${prevDate}`);
        }
    };

    const handleNextAvailable = () => {
        const currentIndex = availableDates.findIndex(d => d === actualDate);
        if (currentIndex - 1 >= 0) {
            const nextDate = availableDates[currentIndex - 1];
            navigate(`/daily-darshan-gallery/${type}/${nextDate}`);
        }
    };

    const isFeatureImage = (index) => index === 0 || index === 3 || index === 7 || index % 8 === 0;

    const typeTitles = {
        'festival-darshan': 'Festival Darshan',
        mangala: 'Mangala Darshan',
        sringar: 'Sringar Darshan',
    };

    const handleDownload = (imageUrl, caption) => {
        const link = document.createElement('a');
        link.href = imageUrl;
        link.download = caption || 'darshan-image';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };
    // const onInit = () => {
    //     console.log('lightGallery has been initialized');
    // };
    return (
        <>
            <PageHeader title="Daily Darshan" imgSrc="/images/page-header/03.webp" />

            <section className="gallery-section py-5" ref={festRef}>
                <div className="container">
                    <div className="row">
                        <div className="col-lg-7 mx-auto">
                            <div className="heading text-center mb-2">
                                <h2 className="head-1">
                                    {type === 'festival-darshan' && festivalTitle
                                        ? `${festivalTitle} - ${moment(actualDate).format('MMMM D, YYYY')}`
                                        : `${typeTitles[type] || ''} - ${moment(actualDate).format('MMMM D, YYYY')}`}
                                </h2>
                            </div>
                        </div>
                        <div className="col-md-5">
                            <div className="gallery-controls rounded-4">
                                <select
                                    value={type}
                                    onChange={(e) => navigate(`/daily-darshan-gallery/${e.target.value}/${date}`)}
                                    className="form-select mb-3 text-center fw-bold text-warning"
                                    style={{ borderRadius: '10px', textAlignLast: 'center', borderColor: '#f5bb3c' }}
                                >
                                    <option value="festival-darshan">Festival Darshan</option>
                                    <option value="sringar">Sringar Darshan</option>
                                </select>
                                <div className="d-flex justify-content-between align-items-center">
                                    <button
                                        onClick={handlePrevAvailable}
                                        className="btn btn-link text-warning text-decoration-none"
                                        disabled={availableDates.findIndex(d => d === actualDate) + 1 >= availableDates.length}
                                    >
                                        <FaArrowLeft className="me-1" style={{ fontSize: '0.9rem' }} /> Previous
                                    </button>
                                    <div className="fw-semibold text-warning" style={{ fontSize: '1.1rem' }}>
                                        {moment(actualDate).format('DD MMMM')}
                                    </div>
                                    <button
                                        onClick={handleNextAvailable}
                                        className="btn btn-link text-warning text-decoration-none"
                                        disabled={availableDates.findIndex(d => d === actualDate) - 1 < 0}
                                        style={{ cursor: availableDates.findIndex(d => d === actualDate) - 1 < 0 ? 'not-allowed' : 'pointer' }}
                                    >
                                        Next <FaArrowRight className="ms-1" style={{ fontSize: '0.9rem' }} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-3">Loading gallery images...</p>
                        </div>
                    ) : error ? (
                        <div className="text-center py-5">
                            <div className="alert alert-info" role="alert">
                                {error}
                            </div>
                        </div>
                    ) : (

                        // Alternative approach - Create a div container and let LightGallery find the images:

                        <div className="container">
                            <div
                                className="gallery-container row"
                                ref={(el) => {
                                    if (el && images.length > 0) {
                                        // Initialize LightGallery on the container
                                        import('lightgallery').then(({ default: lightGallery }) => {
                                            if (!el.lgInstance) {
                                                el.lgInstance = lightGallery(el, {
                                                    speed: 500,
                                                    plugins: [lgThumbnail, lgZoom, lgAutoplay, lgFullscreen, lgRotate, lgShare],
                                                    selector: '.gallery-item'
                                                });
                                            }
                                        });
                                    }
                                }}
                            >
                                {images.map((image, idx) => {
                                    if (!image || !image.url) return null;

                                    return (
                                        <div
                                            key={idx}
                                            className="gallery-item col-lg-4 col-md-6 col-sm-12 mb-4"
                                            data-src={image.url}
                                            data-lg-size="1200-800"
                                            data-sub-html={`<h4>${image.caption || ''}</h4>`}
                                        >
                                            <img
                                                alt={image.caption || `Gallery image ${idx + 1}`}
                                                src={image.url}
                                                className="img-fluid rounded"
                                                style={{
                                                    width: '100%',
                                                    height: '450px',
                                                    objectFit: 'cover',
                                                    cursor: 'pointer'
                                                }}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        // Or the simplest approach - using the original Fancybox that was commented out:
                        // Just uncomment your original Fancybox implementation as it was working fine
                        // <div className="masonry-gallery">
                        //     {images.map((image, idx) => (
                        //         <div className={`masonry-item ${isFeatureImage(idx) ? 'feature-image' : ''}`} key={idx}>
                        //             <div className="image-container" style={{ position: 'relative', overflow: 'hidden', borderRadius: '8px' }}>
                        //                 <Fancybox>
                        //                     <a
                        //                         data-fancybox="gallery"
                        //                         href={image.url}
                        //                         data-download-src={image.url}
                        //                         data-caption={image.caption}
                        //                         className="gallery-link"
                        //                     >
                        //                         <img
                        //                             src={image.url}
                        //                             alt={image.caption}
                        //                             className="img-fluid"
                        //                             loading="lazy"
                        //                             style={{
                        //                                 width: '100%',
                        //                                 height: '100%',
                        //                                 display: 'block',
                        //                                 cursor: 'pointer',
                        //                                 transition: 'transform 0.3s ease'
                        //                             }}
                        //                             onMouseEnter={(e) => {
                        //                                 e.target.style.transform = 'scale(1.05)';
                        //                             }}
                        //                             onMouseLeave={(e) => {
                        //                                 e.target.style.transform = 'scale(1)';
                        //                             }}
                        //                         />
                        //                     </a>
                        //                 </Fancybox>
                        //                 <div
                        //                     className="download-overlay"
                        //                     style={{
                        //                         position: 'absolute',
                        //                         top: '10px',
                        //                         left: '10px',
                        //                         background: 'rgba(0, 0, 0, 0.7)',
                        //                         borderRadius: '50%',
                        //                         padding: '8px',
                        //                         cursor: 'pointer',
                        //                         transition: 'all 0.3s ease',
                        //                         opacity: '0.8',
                        //                         zIndex: 10
                        //                     }}
                        //                     onClick={(e) => {
                        //                         e.preventDefault();
                        //                         e.stopPropagation();
                        //                         handleDownload(image.url, image.caption);
                        //                     }}
                        //                     onMouseEnter={(e) => {
                        //                         e.target.style.background = 'rgba(0, 0, 0, 0.9)';
                        //                         e.target.style.transform = 'scale(1.1)';
                        //                     }}
                        //                     onMouseLeave={(e) => {
                        //                         e.target.style.background = 'rgba(0, 0, 0, 0.7)';
                        //                         e.target.style.transform = 'scale(1)';
                        //                     }}
                        //                     title={`Download ${image.caption}`}
                        //                 >
                        //                     <FaDownload
                        //                         style={{
                        //                             color: 'white',
                        //                             fontSize: '16px',
                        //                             display: 'block'
                        //                         }}
                        //                     />
                        //                 </div>
                        //             </div>
                        //         </div>
                        //     ))}
                        // </div>
                    )}
                </div>
            </section>
            <GoToTop top={top} />
        </>
    );
};

export default GalleryDetailPage;